/**
 * 切割区间编辑器
 *
 * 每行表示一个页码区间「起始页 – 结束页」，可增删行，实时显示该段页数。
 * 区间为 1-based 闭区间，允许不连续、不重叠（纯自定义，不做隐式补齐）。
 *
 * 与 FileDrop 同构：setupXxx(el, opts) 挂载到容器元素，返回一个句柄供外部读写状态。
 */

/** 一行区间（解析后的值） */
export interface RangeRow {
  start: number;
  end: number;
}

export interface RangeEditorHandle {
  /** 当前所有合法区间（非法行被过滤掉） */
  getRows(): RangeRow[];
  /** 设置页码上界（所有已载入文件中的最大页数）；null 表示未知，不做上界校验 */
  setMaxPages(n: number | null): void;
  /**
   * 智能默认值：仅当列表仍是初始未改动状态（单行 1-1）时，
   * 把该行铺满为 1..maxPages，供用户在此基础上直接手改。
   */
  fillToMaxIfUntouched(): void;
  /** 所有行是否都合法 */
  isValid(): boolean;
  /** 首条非法原因（合法时返回 null） */
  getError(): string | null;
  /** 区间总页数（各合法行页数之和） */
  getTotalPages(): number;
  /** 清空回初始状态 */
  reset(): void;
}

/** 行内状态：保留原始输入文本，非法输入时不至于被重绘冲掉 */
interface RowState {
  startRaw: string;
  endRaw: string;
}

const INITIAL: RowState = { startRaw: '1', endRaw: '1' };

/** 解析输入框的值：空串/非法 → null */
function parseNum(v: string): number | null {
  const t = v.trim();
  if (!t) return null;
  const n = Number(t);
  if (!Number.isInteger(n)) return null;
  return n;
}

export function setupRangeEditor(
  el: HTMLElement,
  onChange: (rows: RangeRow[]) => void,
): RangeEditorHandle {
  el.classList.add('ranges');

  const rowsEl = document.createElement('div');
  rowsEl.className = 'range-rows';

  const addBtn = document.createElement('button');
  addBtn.type = 'button';
  addBtn.className = 'ghost range-add';
  addBtn.textContent = '+ 添加区间';

  el.append(rowsEl, addBtn);

  let rows: RowState[] = [{ ...INITIAL }];
  let maxPages: number | null = null;
  /** 用户是否手动改过（用于判断能否套用智能默认值） */
  let touched = false;

  /** 解析一行，得到数值与错误文案 */
  function parseRow(r: RowState): { row: RangeRow; err: string | null } {
    const start = parseNum(r.startRaw);
    const end = parseNum(r.endRaw);
    if (start === null) return { row: { start: NaN, end: NaN }, err: '起始页必须是 ≥1 的整数' };
    if (end === null) return { row: { start, end: NaN }, err: '结束页必须是 ≥1 的整数' };
    if (start < 1) return { row: { start, end }, err: '起始页必须是 ≥1 的整数' };
    if (end < 1) return { row: { start, end }, err: '结束页必须是 ≥1 的整数' };
    if (end < start) return { row: { start, end }, err: '结束页不能小于起始页' };
    if (maxPages !== null && end > maxPages) {
      return { row: { start, end }, err: `结束页超出当前文档页数（${maxPages}）` };
    }
    return { row: { start, end }, err: null };
  }

  function validRows(): RangeRow[] {
    return rows.map(parseRow).filter((p) => p.err === null).map((p) => p.row);
  }

  function emit() {
    onChange(validRows());
  }

  /** 更新某一行的小字说明与错误态（不重绘整表，避免冲掉输入焦点） */
  function refreshRow(i: number) {
    const rowEl = rowsEl.children[i] as HTMLElement | undefined;
    if (!rowEl) return;
    const { row, err } = parseRow(rows[i]);
    const info = rowEl.querySelector<HTMLElement>('.range-info');
    if (info) {
      info.textContent = err ?? `共 ${row.end - row.start + 1} 页`;
      info.classList.toggle('err', err !== null);
    }
    rowEl.classList.toggle('bad', err !== null);
  }

  /** 重绘全部行 */
  function render() {
    rowsEl.innerHTML = '';
    rows.forEach((r, i) => {
      const row = document.createElement('div');
      row.className = 'range-row';

      const start = document.createElement('input');
      start.type = 'number';
      start.min = '1';
      start.className = 'range-start';
      start.value = r.startRaw;
      start.setAttribute('aria-label', `第 ${i + 1} 段的起始页`);

      const dash = document.createElement('span');
      dash.className = 'range-dash';
      dash.textContent = '–';

      const end = document.createElement('input');
      end.type = 'number';
      end.min = '1';
      end.className = 'range-end';
      end.value = r.endRaw;
      end.setAttribute('aria-label', `第 ${i + 1} 段的结束页`);

      const info = document.createElement('span');
      info.className = 'range-info';

      const del = document.createElement('button');
      del.type = 'button';
      del.className = 'range-del';
      del.textContent = '✕';
      del.title = '删除该区间';
      del.disabled = rows.length <= 1;
      del.addEventListener('click', () => {
        rows.splice(i, 1);
        touched = true;
        render();
        emit();
      });

      // 输入即时生效：就地刷新该行的小字说明，不重绘整表
      const onInput = (which: 'start' | 'end') => (e: Event) => {
        const input = e.target as HTMLInputElement;
        const key = which === 'start' ? 'startRaw' : 'endRaw';
        rows[i][key] = input.value;
        touched = true;
        refreshRow(i);
        emit();
      };
      start.addEventListener('input', onInput('start'));
      end.addEventListener('input', onInput('end'));

      row.append(start, dash, end, info, del);
      rowsEl.appendChild(row);
      refreshRow(i);
    });
  }

  addBtn.addEventListener('click', () => {
    // 新行接在上一行之后，保持连续；
    // 结束页默认铺到文档末页（最常见的「1-3 + 剩下的」意图），用户可再手改
    const prevParsed = parseRow(rows[rows.length - 1]);
    const prevEnd = Number.isInteger(prevParsed.row.end) ? prevParsed.row.end : 0;
    const start = maxPages !== null ? Math.min(prevEnd + 1, maxPages) : prevEnd + 1;
    const end = maxPages !== null ? maxPages : start;
    rows.push({ startRaw: String(start), endRaw: String(Math.max(end, start)) });
    touched = true;
    render();
    emit();
    const last = rowsEl.lastElementChild?.querySelector<HTMLInputElement>('.range-start');
    last?.focus();
    last?.select();
  });

  render();
  // 初始即回调一次，让外部的初始状态与编辑器保持一致
  emit();

  return {
    getRows: () => validRows(),
    setMaxPages(n) {
      maxPages = n;
      render();
      emit();
    },
    fillToMaxIfUntouched() {
      if (touched) return;
      if (rows.length !== 1) return;
      const only = rows[0];
      if (only.startRaw !== INITIAL.startRaw || only.endRaw !== INITIAL.endRaw) return;
      if (maxPages === null || maxPages < 1) return;
      only.endRaw = String(maxPages);
      render();
      emit();
    },
    isValid() {
      return rows.length > 0 && rows.every((r) => parseRow(r).err === null);
    },
    getError() {
      for (const r of rows) {
        const e = parseRow(r).err;
        if (e) return e;
      }
      return null;
    },
    getTotalPages() {
      return validRows().reduce((s, r) => s + (r.end - r.start + 1), 0);
    },
    reset() {
      rows = [{ ...INITIAL }];
      touched = false;
      render();
      emit();
    },
  };
}
